import { create } from 'zustand';

import type { UnsplashImage } from '@/components/asset-manager/types';
import http from '@/lib/http';

interface UnsplashCacheEntry {
  images: UnsplashImage[];
  currentPage: number;
  totalPages: number;
}

interface UnsplashStore {
  /** Cache per search query (last loaded). */
  cache: Map<string, UnsplashCacheEntry>;

  /** Return cached data for query if available (no fetch). */
  getCached: (query: string) => UnsplashCacheEntry | undefined;

  /**
   * Load Unsplash images. Returns cached data for (query, page 1) when available and force is false.
   * For append (page > 1) always fetches and does not update cache.
   */
  loadUnsplash: (
    query: string,
    page?: number,
    options?: { append?: boolean; force?: boolean },
  ) => Promise<{ results: UnsplashImage[]; totalPages: number }>;

  clearCache: () => void;
}

export const useUnsplashStore = create<UnsplashStore>((set, get) => ({
  cache: new Map(),

  getCached: (q) => get().cache.get(q),

  loadUnsplash: async (query, page = 1, options = {}) => {
    const { append = false, force = false } = options;
    const isFirstPageRequest = page === 1 && !append;
    const { cache } = get();

    if (isFirstPageRequest) {
      const cached = cache.get(query);
      if (cached && !force) {
        return {
          results: cached.images,
          totalPages: cached.totalPages,
        };
      }
    }

    try {
      const response = await http.get('/unsplash/search', {
        params: { query, page },
      });
      const results = response.data.results ?? [];
      const totalPages = response.data.totalPages ?? 1;

      if (isFirstPageRequest) {
        set((state) => {
          const next = new Map(state.cache);
          next.set(query, {
            images: results,
            currentPage: 1,
            totalPages,
          });
          return { cache: next };
        });
      }

      return { results, totalPages };
    } catch (err) {
      console.error('Failed to fetch Unsplash images:', err);
      throw err;
    }
  },

  clearCache: () => set({ cache: new Map() }),
}));
